import * as Plot from '@observablehq/plot';
import * as d3 from 'd3';
// Import transition module explicitly for type support
import { typography } from '@repo/theme/primitives';
import 'd3-transition';
import { isNil } from 'lodash-es';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { ChartTooltip } from '../../../components/Chart/components/ChartTooltip';
import { formatValue, toTitleCase } from '../utils';
import {
	COMMON_PADDING,
	PIE_CHART_CORNER_RADIUS,
	PIE_CHART_GAP,
	PIE_CHART_MAIN_TEXT_SIZE,
	PIE_CHART_SUB_TEXT_SIZE,
	PIE_CHART_WIDTH_FOR_EACH_PIE,
} from './constants';
import { TooltipContentRenderer } from './TooltipContentRenderer';
import type { ChartRenderFunction, LegendsInfo } from './types';

interface PieDataPoint {
	label: string;
	value: number;
	originalData: Record<string, any>;
	color: string;
}

let tooltipRoot: Root | null = null;
let tooltipContainer: HTMLElement | null = null;

export const renderPieChart: ChartRenderFunction = ({
	results,
	containerRef,
	chartWidth,
	chartHeight,
	theme,
	chartConfigHelper,
}) => {
	const { colors, fontSize } = chartConfigHelper;

	const chartConfig = chartConfigHelper.getAsPieChartConfig();

	const pieLabelType = chartConfig.pieLabelType || 'percent';
	const xAxisColumn = chartConfig.axes.x[0];
	const yAxisColumns = chartConfig.axes.y || [];

	if (!xAxisColumn || yAxisColumns.length === 0) {
		return {
			plot: null,
			legends: {},
		};
	}

	const legends: LegendsInfo = {};

	const validResults = results.filter((result) => !isNil(result[xAxisColumn]));
	const xValues = Array.from(
		new Set(validResults.map((result) => result[xAxisColumn]))
	).sort((a, b) => String(a).localeCompare(String(b)));

	xValues.forEach((xValue, index) => {
		legends[xValue] = {
			color: colors[index % colors.length],
			shortDisplayValue: toTitleCase(String(xValue)),
		};
	});

	const getColor = (d: Record<string, any>) => legends[d[xAxisColumn]].color;

	// Helper function to cleanup tooltip
	const cleanupTooltip = () => {
		if (tooltipRoot) {
			tooltipRoot.unmount();
			tooltipRoot = null;
		}
		if (tooltipContainer) {
			tooltipContainer.remove();
			tooltipContainer = null;
		}
	};

	const pieMark = (
		_index: number[],
		_scales: Plot.ScaleFunctions,
		_values: Plot.ChannelValues,
		dimensions: Plot.Dimensions,
		context: Plot.Context
	) => {
		const svg = context.ownerSVGElement;
		if (!svg) return null;

		const startX = dimensions.marginLeft;
		const startY = dimensions.marginTop;
		const width =
			dimensions.width - dimensions.marginLeft - dimensions.marginRight;
		const height =
			dimensions.height - dimensions.marginTop - dimensions.marginBottom;

		// Clear any existing pie chart content
		d3.select(svg).selectAll('.pie-chart-container').remove();

		const container = d3
			.select(svg)
			.append('g')
			.attr('class', 'pie-chart-container');

		const outerMostRadius = Math.min(width, height) / 2;

		const centerX = startX + width / 2;
		const centerY = startY + height / 2;

		// Calculate total sum for the first column only
		let totalSum = 0;
		if (yAxisColumns.length > 0) {
			const [firstColumn] = yAxisColumns;
			totalSum = d3.sum(
				validResults
					.filter((result) => !isNil(result[firstColumn]))
					.map((result) => result[firstColumn] as number)
			);
		}

		const tooltipRenderer = (
			x: number,
			y: number,
			legend: {
				name: string;
				color: string;
				shortDisplayValue?: string | null;
			},
			valueFormatted: string
		) => {
			const legendPair = {
				legend: {
					name: legend.name,
					color: legend.color,
					shortDisplayValue: legend.shortDisplayValue,
				},
				valueFormatted,
			};

			return (
				<ChartTooltip x={x} y={y}>
					<TooltipContentRenderer
						groupValue={legend.name}
						legendAndValuePairs={[legendPair]}
					/>
				</ChartTooltip>
			);
		};

		yAxisColumns.forEach((yColumn, columnIndex) => {
			const pieData = validResults
				.filter((result) => !isNil(result[yColumn]))
				.map((result) => ({
					label: String(result[xAxisColumn]),
					value: result[yColumn],
					originalData: result,
					color: getColor(result),
				}));

			// Calculate total for percentage calculations
			const total = d3.sum(pieData, (d) => d.value);

			// Set up dimensions
			const radius =
				outerMostRadius - columnIndex * PIE_CHART_WIDTH_FOR_EACH_PIE;
			const innerRadius = radius - PIE_CHART_WIDTH_FOR_EACH_PIE;

			// Calculate padAngle to achieve 3px spacing
			const spacingInPixels = 3;
			const padAngle = spacingInPixels / radius;

			const calculateLabel = (d: d3.PieArcDatum<PieDataPoint>) => {
				const { value } = d.data;
				const columnFormat = chartConfig.columnConfigs[yColumn];
				const formattedValue = formatValue(value, columnFormat);

				if (pieLabelType === 'number') {
					return formattedValue;
				}

				const percentage = ((value / total) * 100).toFixed(1);
				return `${percentage}%`;
			};

			const g = container
				.append('g')
				.attr('transform', `translate(${centerX}, ${centerY})`);

			const pie = d3
				.pie<PieDataPoint>()
				.value((d) => d.value)
				.sort((a, b) => a.label.localeCompare(b.label))
				.padAngle(padAngle);

			const arc = d3
				.arc<d3.PieArcDatum<PieDataPoint>>()
				.innerRadius(innerRadius)
				.outerRadius(radius)
				.cornerRadius(PIE_CHART_CORNER_RADIUS);

			// Generate pie slices
			const arcs = pie(pieData);

			// Create pie slices
			const slices = g
				.selectAll('.slice')
				.data(arcs)
				.enter()
				.append('g')
				.attr('class', 'slice');

			// Add paths (pie slices)
			slices
				.append('path')
				.attr('d', arc)
				.attr('fill', (d) => d.data.color)
				.style('cursor', 'pointer')
				.on('mouseenter', (_event, d) => {
					cleanupTooltip();

					const midAngle = (d.startAngle + d.endAngle) / 2;
					const customRadius = innerRadius + (radius - innerRadius) * 0.55;
					const midAngleX = Math.cos(midAngle - Math.PI / 2) * customRadius;
					const midAngleY = Math.sin(midAngle - Math.PI / 2) * customRadius;

					if (context.ownerSVGElement && context.ownerSVGElement.parentNode) {
						// eslint-disable-next-line no-param-reassign
						(context.ownerSVGElement.parentNode as HTMLElement).style.position =
							'relative';

						// Convert SVG coordinates to screen coordinates
						const svgElement = context.ownerSVGElement;
						const svgX = centerX + midAngleX;
						const svgY = centerY + midAngleY;
						const ctm = svgElement.getScreenCTM();
						if (ctm) {
							const screenPoint = new DOMPoint(svgX, svgY).matrixTransform(ctm);
							const { x, y } = screenPoint;

							const reactElement: React.ReactElement | null = tooltipRenderer(
								x,
								y,
								{
									name: d.data.label,
									color: d.data.color,
									shortDisplayValue: legends[d.data.label].shortDisplayValue,
								},
								calculateLabel(d)
							);

							if (reactElement) {
								tooltipContainer = document.createElement('div');
								context.ownerSVGElement.parentNode.appendChild(
									tooltipContainer
								);
								tooltipRoot = createRoot(tooltipContainer);
								tooltipRoot.render(reactElement);
							}
						}
					}
				})
				.on('mouseleave', cleanupTooltip);
		});

		if (totalSum > 0 && yAxisColumns.length > 0) {
			const [primaryColumn] = yAxisColumns;
			const columnFormat = chartConfig.columnConfigs[primaryColumn];
			const formattedTotal = formatValue(totalSum, columnFormat);
			const columnName = toTitleCase(primaryColumn);

			const centerTextGroup = container
				.append('g')
				.attr('transform', `translate(${centerX}, ${centerY})`);

			const textElement = centerTextGroup
				.append('text')
				.attr('text-anchor', 'middle')
				.attr('dominant-baseline', 'middle')
				.attr('dy', `-${PIE_CHART_GAP / 2}px`)
				.style('font-size', `${PIE_CHART_MAIN_TEXT_SIZE}px`)
				.style('font-weight', typography.weight.bold)
				.style('fill', theme.other.getColor('text/primary/default'))
				.text(formattedTotal);

			if (theme.fontFamily) {
				textElement.style('font-family', theme.fontFamily);
			}

			// Add subtitle with column name
			const subtitleElement = centerTextGroup
				.append('text')
				.attr('text-anchor', 'middle')
				.attr('dominant-baseline', 'middle')
				.attr('dy', `${PIE_CHART_GAP / 2}px`)
				.style('font-size', `${PIE_CHART_SUB_TEXT_SIZE}px`)
				.style('font-weight', typography.weight.regular)
				.style('fill', theme.other.getColor('text/secondary/default'))
				.text(columnName);

			if (theme.fontFamily) {
				subtitleElement.style('font-family', theme.fontFamily);
			}
		}

		const node = container.node();
		return node || null;
	};

	const marks: Plot.Markish[] = [pieMark];
	const plot = Plot.plot({
		marks,
		width: chartWidth,
		height: chartHeight,
		margin: COMMON_PADDING,
		style: {
			background: 'transparent',
			fontFamily: theme.fontFamily,
			fontSize: `${fontSize}px`,
		},
	});

	containerRef.current?.appendChild(plot);

	return {
		plot,
		legends,
	};
};
